import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth.js';
import { SidebarProvider } from './hooks/useSidebar.js';
import { AppLayout } from './components/AppLayout.js';
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { CatalogsPage } from './pages/CatalogsPage.js';
import { CatalogDetailPage } from './pages/CatalogDetailPage.js';
import { ResellersPage } from './pages/ResellersPage.js';
import { QuotesPage } from './pages/QuotesPage.js';
import { QuoteBuilderPage } from './pages/QuoteBuilderPage.js';
import { AccountPage } from './pages/AccountPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { UsersPage } from './pages/UsersPage.js';
import { IdeasPage } from './pages/IdeasPage.js';
import { BugReportsPage } from './pages/BugReportsPage.js';
import { MarketplacePage } from './pages/MarketplacePage.js';
import { MarketplaceAdminPage } from './pages/MarketplaceAdminPage.js';
import { IncomingQuotesPage } from './pages/IncomingQuotesPage.js';
import { IncomingQuoteDetailPage } from './pages/IncomingQuoteDetailPage.js';
import { CollateralPage } from './pages/CollateralPage.js';
import { AdminAssociationsPage } from './pages/AdminAssociationsPage.js';
import { PrivacyPage } from './pages/PrivacyPage.js';
import { TermsPage } from './pages/TermsPage.js';

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <SidebarProvider>
                  <AppLayout />
                </SidebarProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="catalogs" element={<CatalogsPage />} />
            <Route path="catalogs/:id" element={<CatalogDetailPage />} />
            <Route path="quotes" element={<QuotesPage />} />
            <Route path="quotes/new" element={<QuoteBuilderPage />} />
            <Route path="quotes/:id" element={<QuoteBuilderPage />} />
            <Route path="incoming-quotes" element={<IncomingQuotesPage />} />
            <Route path="incoming-quotes/:id" element={<IncomingQuoteDetailPage />} />
            <Route path="resellers" element={<ResellersPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="marketplace" element={<MarketplacePage />} />
            <Route path="marketplace-admin" element={<MarketplaceAdminPage />} />
            <Route path="admin/associations" element={<AdminAssociationsPage />} />
            <Route path="collateral" element={<CollateralPage />} />
            <Route path="ideas" element={<IdeasPage />} />
            <Route path="bugs" element={<BugReportsPage />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
